import { makeStyles } from "tss-react/mui";
import { ICONS_WEB } from "@miplatform/constants";
import * as mdi from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";
import { MiIconButton } from "../MiIconButton/MiIconButton";
import { MiMenu } from "../MiMenu/MiMenu";

function DropIcon({ dropType }) {
  const { classes } = useStyles();

  const iconPath = ICONS_WEB.DROP_LABOR;

  return (
    <Box className={classes.dropIconContainer}>
      <Typography variant="subtitle2">DROP TYPE</Typography>
      <MiIcon path={mdi[iconPath]} size={0.9} />
      <Typography variant="subtitle2">{dropType.toUpperCase()}</Typography>
    </Box>
  );
}

function LabeledText({ label, text }) {
  const { classes } = useStyles();
  const theme = useTheme();

  return (
    <Box display="flex" marginRight={theme.spacing(2)} flexWrap="nowrap">
      <Typography
        variant="subtitle2"
        marginRight={theme.spacing(1)}
        color={theme.palette.grey.main}
        className={classes.ellipsis}
      >
        {label}
      </Typography>
      <Typography variant="subtitle2">{text}</Typography>
    </Box>
  );
}

/**
 * MiDropCard component displays a card with drop information, including name, description, drop type, and prices.
 * It also provides actions and toggle functionality.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.drop - The drop object.
 * @param {string} props.drop.name - The name of the drop.
 * @param {string} props.drop.description - The description of the drop.
 * @param {string} props.drop.dropType - The type of the drop.
 * @param {Array} props.drop.prices - The list of prices associated with the drop.
 * @param {boolean} props.expanded - Indicates if the card is expanded.
 * @param {Function} props.onToggle - Callback function to handle toggle action.
 * @param {Function} props.onSelect - Callback function to handle select action.
 * @param {boolean} props.isSelected - Indicates if the card is selected.
 * @param {boolean} props.disabled - Indicates if the card is disabled.
 * @param {Array} [props.actions=[]] - List of actions available for the card.
 *
 * @returns {JSX.Element} The rendered MiDropCard component.
 */
export function MiDropCard({
  drop = {},
  expanded = false,
  onToggle,
  onSelect,
  isSelected = false,
  disabled = false,
  actions = [],
  isConfigured = false,
  isAccountConfigured = false,
}) {
  const { classes } = useStyles();
  const theme = useTheme();
  const { name, description, dropType, prices = [], pricingOverrides } = drop;

  const getCardBgColor = () => {
    if (isConfigured) {
      return theme.palette.green.light;
    }
    if (isAccountConfigured) {
      return theme.palette.common.white;
    }
    return theme.palette.orange.light;
  };

  const typographyColor =
    isConfigured || isAccountConfigured
      ? theme.palette.primary.main
      : theme.palette.orange.dark;

  const items = prices.length === 0 ? pricingOverrides : prices;

  return (
    <Box
      className={`${classes.cardContainer} ${
        isSelected ? classes.selected : ""
      }`}
      onClick={() => {
        if (!disabled) {
          onSelect({ ...drop });
        }
      }}
      style={{ cursor: onSelect && "pointer" }}
      backgroundColor={getCardBgColor()}
      padding={theme.spacing(2)}
      height={84}
    >
      <Box className={classes.content}>
        <DropIcon dropType={dropType} />
        <Box className={classes.textContent}>
          <Typography
            color={typographyColor}
            fontSize={theme.fontSize.large}
            fontWeight={theme.fontWeight.semiBold}
          >
            {name}
          </Typography>
          {description && (
            <LabeledText label="Description" text={description} />
          )}
          <Box className={classes.pricing}>
            {items?.map((item) => (
              <LabeledText
                key={item.id || item.priceTemplateId}
                label={item.priceName}
                text={`$${
                  item.overridePrice || item.defaultPrice || item.price
                }`}
              />
            ))}
          </Box>
        </Box>
      </Box>
      <Box
        className={classes.actionContainer}
        onClick={(e) => e.stopPropagation()}
      >
        {onToggle && (
          <MiIconButton onClick={onToggle}>
            <MiIcon
              path={
                expanded
                  ? mdi[ICONS_WEB.CHEVRON_UP]
                  : mdi[ICONS_WEB.CHEVRON_DOWN]
              }
            />
          </MiIconButton>
        )}
        {actions.length > 0 && <MiMenu data={actions} />}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  cardContainer: {
    display: "flex",
    justifyContent: "space-between",
    border: `2px solid transparent`,
    borderRadius: 6,
    margin: theme.spacing(3),
    boxShadow: "0px 1px 2px 0px rgba(0,0,0,.2)",
    overflow: "hidden",
  },
  content: {
    display: "flex",
  },
  textContent: {
    display: "flex",
    flexDirection: "column",
    marginLeft: theme.spacing(4),
  },
  actionContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  selected: {
    border: `2px solid ${theme.palette.orange.main}`,
  },
  pricing: {
    display: "flex",
    "& > *": {
      paddingRight: theme.spacing(0),
    },
  },
  ellipsis: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  dropIconContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 62,
    width: 80,
    borderRadius: 8,
    backgroundColor: theme.palette.blue.light,
    color: theme.palette.blue.dark,
    "& > *": {
      fontSize: theme.fontSize.small,
      fontWeight: theme.fontWeight.medium,
      whiteSpace: "nowrap",
    },
  },
}));
